import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import AuthenticatedImage from '../../../components/AuthenticatedImage';
import Button from '../../../components/ui/Button';
import attendanceService from '../../../services/attendanceService';
import NotificationToast from '../../../components/ui/NotificationToast';
import { useEmployeeSelfService } from '../../../contexts/EmployeeSelfServiceContext';
import { formatTimeFromApi } from '../../../utils/attendanceDateTime';
import { buildAttendancePhotoUrl, resolveMediaUrl, getPunchState } from '../../../utils/attendanceHelpers';

const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const { employee, todayStatus, loading: ctxLoading, isLocalAuth } = useEmployeeSelfService();
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationStatus, setLocationStatus] = useState('checking');
  const [cameraStatus, setCameraStatus] = useState('checking');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    checkPermissions();
  }, []);

  useEffect(() => {
    if (employee?.id && !isLocalAuth) {
      loadRecentActivities(employee.id);
    } else {
      setLoading(false);
    }
  }, [employee, isLocalAuth]);

  const loadRecentActivities = async (employeeId) => {
    try {
      setLoading(true);
      const result = await attendanceService.getAttendanceRecords({
        employee_id: employeeId,
        limit: 5
      });

      if (result.success && result.data) {
        const activities = [];
        result.data.forEach(record => {
          if (record.punches?.length) {
            record.punches.forEach((punch, index) => {
              activities.push({
                id: `${record.id}_${punch.id || index}`,
                type: punch.punch_type === 'IN' ? 'punch-in' : 'punch-out',
                timestamp: punch.punch_time,
                attendanceDate: record.attendance_date,
                location: punch.location_address || 'Location not available',
                selfie: null,
                selfieFallback: resolveMediaUrl(punch.photo_url),
                duration: punch.punch_type === 'OUT' && record.work_hours ? `${record.work_hours} hrs` : null,
              });
            });
            return;
          }

          if (record.clock_in_time) {
            activities.push({
              id: `${record.id}_in`,
              type: 'punch-in',
              timestamp: record.clock_in_time,
              attendanceDate: record.attendance_date,
              location: record.clock_in_location_address || 'Location not available',
              selfie: buildAttendancePhotoUrl(record.id, 'clock_in'),
              selfieFallback: resolveMediaUrl(record.clock_in_photo_url),
              duration: null
            });
          }
          if (record.clock_out_time) {
            activities.push({
              id: `${record.id}_out`,
              type: 'punch-out',
              timestamp: record.clock_out_time,
              attendanceDate: record.attendance_date,
              location: record.clock_out_location_address || 'Location not available',
              selfie: buildAttendancePhotoUrl(record.id, 'clock_out'),
              selfieFallback: resolveMediaUrl(record.clock_out_photo_url),
              duration: record.work_hours ? `${record.work_hours} hrs` : null
            });
          }
        });

        activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setRecentActivities(activities.slice(0, 5));
      }
    } catch (err) {
      console.error('Error loading recent activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkPermissions = async () => {
    // Check location permission
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationStatus('granted');
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        () => {
          setLocationStatus('denied');
        }
      );
    } else {
      setLocationStatus('unavailable');
    }

    // Check camera permission
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setCameraStatus('granted');
    } catch (error) {
      setCameraStatus('denied');
    }
  };

  const formatTime = (dateString, attendanceDate = '') => {
    if (!dateString) return '--:--';
    const wall = formatTimeFromApi(dateString, attendanceDate);
    if (wall && wall !== '-') return wall;
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'granted':
        return { name: 'CheckCircle2', className: 'text-success' };
      case 'denied':
        return { name: 'XCircle', className: 'text-error' };
      case 'checking':
        return { name: 'Loader2', className: 'text-muted-foreground animate-spin' };
      default:
        return { name: 'AlertCircle', className: 'text-warning' };
    }
  };

  const canPunch = locationStatus === 'granted' && cameraStatus === 'granted';
  const punchState = getPunchState(todayStatus);
  const isPunchedIn = punchState.isPunchedIn;

  if (loading || ctxLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
          Attendance Dashboard
        </h1>
        <p className="text-xs md:text-sm lg:text-base text-muted-foreground">
          Manage your daily attendance and track work hours
        </p>
      </div>

      {/* Status Card */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center flex-shrink-0 ${isPunchedIn ? 'bg-success/10' : 'bg-muted'
              }`}>
              <Icon
                name={isPunchedIn ? "CheckCircle2" : "Clock"}
                size={20}
                className={`${isPunchedIn ? "text-success" : "text-muted-foreground"} md:w-6 md:h-6`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base md:text-lg lg:text-xl font-semibold text-foreground">
                {isPunchedIn ? "Currently Punched In" : "Not Punched In"}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 truncate">
                {todayStatus?.last_activity || (punchState.punchCount > 0
                  ? `${punchState.punchCount} punch${punchState.punchCount !== 1 ? 'es' : ''} today`
                  : 'No recent activity')}
              </p>
            </div>
          </div>
          <div className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg flex-shrink-0 ${isPunchedIn ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
            }`}>
            <span className="text-xs font-medium uppercase tracking-wide">
              {isPunchedIn ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>

      {/* Punch Action Card */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 md:mb-6">
          Punch {isPunchedIn ? 'Out' : 'In'} Action
        </h3>

        <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
          <div className="flex items-center justify-between p-3 md:p-4 bg-muted/50 rounded-lg gap-2">
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
              <Icon name="MapPin" size={18} className="text-foreground flex-shrink-0 md:w-5 md:h-5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs md:text-sm font-medium text-foreground">GPS Location</p>
                {currentLocation && (
                  <p className="text-xs text-muted-foreground mt-1 break-all">
                    Lat: {typeof currentLocation.latitude === 'number' ? currentLocation.latitude.toFixed(6) : parseFloat(currentLocation.latitude).toFixed(6)}, Lng: {typeof currentLocation.longitude === 'number' ? currentLocation.longitude.toFixed(6) : parseFloat(currentLocation.longitude).toFixed(6)}
                  </p>
                )}
              </div>
            </div>
            <Icon
              name={getStatusIcon(locationStatus).name}
              size={20}
              className={`${getStatusIcon(locationStatus).className} flex-shrink-0 md:w-6 md:h-6`}
            />
          </div>

          <div className="flex items-center justify-between p-3 md:p-4 bg-muted/50 rounded-lg gap-2">
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
              <Icon name="Camera" size={18} className="text-foreground flex-shrink-0 md:w-5 md:h-5" />
              <p className="text-xs md:text-sm font-medium text-foreground">Camera Access</p>
            </div>
            <Icon
              name={getStatusIcon(cameraStatus).name}
              size={20}
              className={`${getStatusIcon(cameraStatus).className} flex-shrink-0 md:w-6 md:h-6`}
            />
          </div>
        </div>

        {!canPunch && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
            <p className="text-sm text-error">
              {locationStatus !== 'granted' && 'Location permission is required. '}
              {cameraStatus !== 'granted' && 'Camera permission is required. '}
              Please enable access in your browser settings.
            </p>
          </div>
        )}

        <Button
          variant={isPunchedIn ? "destructive" : "default"}
          size="lg"
          fullWidth
          disabled={!canPunch}
          onClick={() => navigate(isPunchedIn ? '/erp/selfie-attendance/punch-out' : '/erp/selfie-attendance/punch-in')}
          iconName={isPunchedIn ? "LogOut" : "LogIn"}
          iconPosition="left"
        >
          {isPunchedIn ? 'Punch Out' : 'Punch In'}
        </Button>
      </div>

      {/* Today's Summary */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Calendar" size={18} className="text-accent md:w-5 md:h-5" />
          </div>
          <h3 className="text-base md:text-lg font-semibold text-foreground">Today's Summary</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Punch In</p>
            </div>
            <p className="text-xl font-semibold text-foreground">
              {formatTime(
                todayStatus?.current_punch_in_time || todayStatus?.record?.clock_in_time,
                todayStatus?.record?.attendance_date
              )}
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Punch Out</p>
            </div>
            <p className="text-xl font-semibold text-foreground">
              {formatTime(todayStatus?.record?.clock_out_time, todayStatus?.record?.attendance_date)}
            </p>
          </div>

          <div className="bg-primary/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Timer" size={16} className="text-primary" />
              <p className="text-xs text-primary">Total Hours</p>
            </div>
            <p className="text-xl font-semibold text-primary">
              {todayStatus?.record?.work_hours ? `${todayStatus.record.work_hours} hrs` : '0.00 hrs'}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6 gap-2">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="History" size={18} className="text-secondary md:w-5 md:h-5" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-foreground">Recent Activity</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/erp/employee-self-service/attendance-details')}
            iconName="ArrowRight"
            iconPosition="right"
            className="flex-shrink-0"
          >
            <span className="hidden sm:inline">View All</span>
            <span className="sm:hidden">All</span>
          </Button>
        </div>

        {recentActivities.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No recent attendance activity</p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {recentActivities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center gap-2 md:gap-4 p-3 md:p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                {activity.selfie && (
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <AuthenticatedImage
                      src={activity.selfie}
                      fallbackSrc={activity.selfieFallback}
                      alt={`${activity.type} selfie`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1.5 md:gap-2 mb-1 flex-wrap">
                    <span className={`px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs font-medium flex-shrink-0 ${activity.type === 'punch-in' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                      }`}>
                      {activity.type === 'punch-in' ? 'In' : 'Out'}
                    </span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(activity.timestamp)}
                    </span>
                  </div>
                  <p className="text-xs md:text-sm font-medium text-foreground mb-1">
                    {formatTime(activity.timestamp, activity.attendanceDate)}
                  </p>
                  <div className="flex items-center gap-1.5 md:gap-2 text-xs text-muted-foreground min-w-0">
                    <Icon name="MapPin" size={12} className="flex-shrink-0 md:w-3.5 md:h-3.5" />
                    <span className="truncate">{activity.location}</span>
                  </div>
                </div>

                {activity.duration && (
                  <div className="bg-card rounded-lg px-2 py-1.5 md:px-3 md:py-2 flex-shrink-0">
                    <p className="text-xs text-muted-foreground mb-0.5 md:mb-1">Duration</p>
                    <p className="text-xs md:text-sm font-semibold text-foreground">
                      {activity.duration}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-3 md:mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          <button
            onClick={() => navigate('/erp/employee-self-service/attendance-details')}
            className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors text-left"
          >
            <Icon name="Calendar" size={18} className="text-primary flex-shrink-0 md:w-5 md:h-5" />
            <div className="min-w-0">
              <p className="text-sm md:text-base font-medium text-foreground">Attendance History</p>
              <p className="text-xs text-muted-foreground hidden sm:block">View your complete attendance records</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/erp/employee-self-service/leave')}
            className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors text-left"
          >
            <Icon name="CalendarDays" size={18} className="text-primary flex-shrink-0 md:w-5 md:h-5" />
            <div className="min-w-0">
              <p className="text-sm md:text-base font-medium text-foreground">Leave Management</p>
              <p className="text-xs text-muted-foreground hidden sm:block">Apply for leave and track applications</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/erp/employee-self-service/payslip')}
            className="flex items-center gap-2 md:gap-3 p-3 md:p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors text-left"
          >
            <Icon name="FileText" size={18} className="text-primary flex-shrink-0 md:w-5 md:h-5" />
            <div className="min-w-0">
              <p className="text-sm md:text-base font-medium text-foreground">Payslips</p>
              <p className="text-xs text-muted-foreground hidden sm:block">View and download salary statements</p>
            </div>
          </button>
        </div>
      </div>

      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onDismiss={() => setNotification(null)}
          position="top-right"
          autoHideDuration={3000}
        />
      )}
    </div>
  );
};

export default AttendanceDashboard;

